import * as turf from '@turf/turf'
import { GEOJSON_COORDINATE_PRECISION } from 'api-validations/geojson-schemas'
import type { Feature, LineString, Point, Position } from 'geojson'

const precisionOptions = {
  /**
   * Tolerance for point-on-line detection and snapping, measured in degrees
   *
   * TODO: it would be nice if we could derive this from GEOJSON_COORDINATE_PRECISION (10 ** -GEOJSON_COORDINATE_PRECISION)
   * But there's a bug right now that is always present but harder to notice if you have a lower tolerance/higher precision.
   */
  epsilon: 10 ** -GEOJSON_COORDINATE_PRECISION
}

type EdgeCrossing = {
  index: number
  point: Feature<Point>
}

type Coordinate = Position
type Ring = Coordinate[]
type Line = Coordinate[]

type Cut = {
  from: EdgeCrossing
  to: EdgeCrossing
}

const areRoughlyEqual = (
  p1: number[],
  p2: number[],
  epsilon = precisionOptions.epsilon
): boolean => {
  const dx = p1[0] - p2[0]
  const dy = p1[1] - p2[1]
  const distanceSquared = dx * dx + dy * dy
  return distanceSquared < epsilon * epsilon
}

const distanceToSegment = (
  point: Position,
  start: Position,
  end: Position
): { distance: number; t: number } => {
  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  const lengthSquared = dx * dx + dy * dy

  if (lengthSquared === 0) {
    return {
      distance: Math.hypot(point[0] - start[0], point[1] - start[1]),
      t: 0
    }
  }

  const t = ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / lengthSquared
  const clampedT = Math.max(0, Math.min(1, t))
  const closestPoint: Position = [start[0] + clampedT * dx, start[1] + clampedT * dy]

  return {
    distance: Math.hypot(point[0] - closestPoint[0], point[1] - closestPoint[1]),
    t
  }
}

// Helper function to insert a vertex into a ring at the appropriate position
const insertVertexIntoRing = (vertex: Position, rings: Position[][]): Position[][] => {
  let minDist = Infinity
  let closestRingIdx = -1
  let closestEdgeIdx = -1

  // Find closest edge on any ring
  for (let ringIdx = 0; ringIdx < rings.length; ringIdx++) {
    const ring = rings[ringIdx]
    for (let i = 0; i < ring.length - 1; i++) {
      const dist = turf.pointToLineDistance(
        turf.point(vertex),
        turf.lineString([ring[i], ring[i + 1]]),
        { units: 'degrees' }
      )
      if (dist < minDist) {
        minDist = dist
        closestRingIdx = ringIdx
        closestEdgeIdx = i
      }
    }
  }

  // If too far -- i.e., not within espilon, so not a point "on" that edge -- don't insert
  if (minDist > precisionOptions.epsilon) return rings

  // Check if the vertex already exists in that ring (i.e., is equal to one of the edge endpoints)
  const ring = rings[closestRingIdx]
  if (
    areRoughlyEqual(vertex, ring[closestEdgeIdx]) ||
    areRoughlyEqual(vertex, ring[closestEdgeIdx + 1])
  ) {
    return rings
  }

  // Insert at the closest edge
  const newRing = ring.toSpliced(closestEdgeIdx + 1, 0, vertex)

  return rings.with(closestRingIdx, newRing)
}

const findEdge = (ring: Ring | Line, point: Feature<Point>): EdgeCrossing | null => {
  let bestMatch: EdgeCrossing | null = null
  let bestDistance = Infinity

  // Check each edge (excluding the closing edge, since it's a duplicate of the first)
  for (let i = 0; i < ring.length - 1; i++) {
    const start = ring[i]
    const end = ring[i + 1]
    const { distance, t } = distanceToSegment(point.geometry.coordinates, start, end)
    if (distance <= precisionOptions.epsilon && t >= 0 && t <= 1 && distance < bestDistance) {
      bestDistance = distance
      bestMatch = { index: i, point: point }
    }
  }

  return bestMatch
}

const splitRing = (
  ring: Ring,
  edges: { first: EdgeCrossing; second: EdgeCrossing; connectingCut: Line }
): [Ring, Ring] => {
  const { first, second } = edges

  if (first.index === second.index) {
    throw new Error('Degenerate cut: both crossings on the same edge')
  }

  const isCutInDirectionOfRing = first.index < second.index

  if (!isCutInDirectionOfRing) {
    // Swap to ensure first comes before second in ring order
    return splitRing(ring, {
      first: edges.second,
      second: edges.first,
      connectingCut: edges.connectingCut.slice().toReversed()
    })
  }

  // Path along boundary from first to second crossing
  const firstSegment = [
    first.point.geometry.coordinates,
    ...ring.slice(first.index + 1, second.index + 1),
    second.point.geometry.coordinates
  ]

  // Path around boundary from second back to first crossing
  const rest = [
    second.point.geometry.coordinates,
    ...ring.slice(second.index + 1, -1), // After second to end (excluding duplicate)
    ...ring.slice(0, first.index + 1), // Start to first
    first.point.geometry.coordinates
  ]

  // interior of the cut (excluding endpoints, since they're included in the segments)
  const interior = edges.connectingCut.slice(1, -1)

  // Close each path with the cut line to form polygons
  const ring1 = [...firstSegment, ...interior.toReversed(), firstSegment[0]]

  const ring2 = [...rest, ...interior, rest[0]]

  return [ring1, ring2]
}

const lineIntersect = (a: Feature<LineString>, b: Feature<LineString>): Feature<Point>[] => {
  const result = turf.lineIntersect(a, b).features
  const startB = turf.point(b.geometry.coordinates[0])
  const endB = turf.point(b.geometry.coordinates[b.geometry.coordinates.length - 1])

  if (
    turf.booleanPointOnLine(startB, a, precisionOptions) &&
    !result.find((pt) => turf.booleanEqual(pt, startB))
  ) {
    result.push(startB)
  }
  if (
    turf.booleanPointOnLine(endB, a, precisionOptions) &&
    !result.find((pt) => turf.booleanEqual(pt, endB))
  ) {
    result.push(endB)
  }

  return result
}

const sequentialIntersections = (ring: Ring, splitter: Feature<LineString>): Feature<Point>[] => {
  const boundary = turf.lineString(ring)
  const crossings = lineIntersect(boundary, splitter)

  // the crossings are not ordered sequentially along the splitter line,
  // so we need to sort them by their distance along the splitter
  const sortedCrossings = crossings.sort((a, b) => {
    const distA = turf.length(
      turf.lineSlice(splitter.geometry.coordinates[0], a.geometry.coordinates, splitter)
    )
    const distB = turf.length(
      turf.lineSlice(splitter.geometry.coordinates[0], b.geometry.coordinates, splitter)
    )
    return distA - distB
  })

  return sortedCrossings
}

/**
 * Extracts a segment of a line between two points.
 *
 * Reimplements turf.lineSlice to avoid precision issues with lat/lon coordinates.
 * Uses turf.lineSplit internally, which uses an rbush for spatial indexing.
 *
 * @throws if the line cannot be split at the start or end points.
 */
const lineSlice = (start: Feature<Point>, end: Feature<Point>, line: Feature<LineString>) => {
  const firstSegment = turf.lineSplit(line, start).features.at(-1)
  if (!firstSegment) {
    throw new Error('Failed to split line at start point')
  }
  return turf.lineSplit(firstSegment, end).features[0]
}

const findCutsInternal = (ring: Ring, splitter: Feature<LineString>): Cut[] => {
  const crossings = sequentialIntersections(ring, splitter)
  const cuts: Cut[] = []
  const poly = turf.polygon([ring])

  // Adjacent crossings along the splitter define candidate inside/outside intervals.
  // We cannot assume valid cuts are always (0,1), (2,3), ... because tangential
  // touches can introduce an extra crossing that shifts the true enter/exit pair.
  for (let i = 0; i < crossings.length - 1; i++) {
    const start = crossings[i]
    const end = crossings[i + 1]
    const midpoint = turf.midpoint(start, end)

    if (turf.booleanPointInPolygon(midpoint, poly, { ignoreBoundary: true })) {
      const from = findEdge(ring, start)
      const to = findEdge(ring, end)
      if (from === null || to === null) {
        continue
      }
      if (from.index === to.index) {
        continue // degenerate: both crossings on same edge
      }

      cuts.push({ from, to })
    }
  }

  return cuts
}

export const Line = {
  findSegment: findEdge, // find which segment contains a point
  insertVertex: insertVertexIntoRing, // insert point where it lies on line
  intersect: lineIntersect, // find intersections between two lines
  intersections: sequentialIntersections, // find all intersections with another line
  slice: lineSlice, // extract subsegment between two points
  areRoughlyEqual: areRoughlyEqual
}

export namespace Ring {
  export type Ring = Line
  export const findCuts = findCutsInternal // find valid cutting pairs (requires polygon semantics)
  export const split = splitRing // split into two closed rings
}
